import axios from "axios";
import store from "@/store";
import { getRefreshToken, getToken, isTokenExpired } from "@/utils/auth";
import { app } from "../main";

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 1000000, // request timeout
});

// Request interceptor
service.interceptors.request.use(
  async config => {
    if (config.skipAuthRefresh) {
      return config;
    }

    const headers = config.headers || (config.headers = {});
    let token = getToken();
    const hasRefreshToken = !!getRefreshToken();
    const isExpired = isTokenExpired();

    if (token && !isExpired) {
      headers['Authorization'] = `Bearer ${token}`;
      return config;
    }

    if (hasRefreshToken) {
      try {
        await store.dispatch('user/refreshToken', { redirectOnFailure: false });
        token = getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (e) {
        // If refresh fails, do not set header (will be handled by response interceptor)
        console.error('Error refreshing access token before request:', e);
      }
    }

    return config;
  },
  error => {
    store.commit('message/showMessage', { 
      message: "خطأ في الطلب", 
      type: "error" 
    });
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor
service.interceptors.response.use(
  response => {
    // Show success message for 201 status
    if (response.status === 201) {
      store.commit('message/showMessage', { 
        message: "تمت العملية بنجاح", 
        type: "success" 
      });
    }
    return response.data;
  },
  async error => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if response body contains redirectTo=/login
      /*const responseData = error.response.data;
      const shouldForceLogout = responseData && 
        (responseData.redirectTo === '/login' || 
         (typeof responseData === 'string' && responseData.includes('redirectTo=/login')));

      if (shouldForceLogout) {
        // Force logout immediately without attempting token refresh
        processQueue(error, null);
        await handleLogout();
        return Promise.reject(error);
      }*/

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return service(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await store.dispatch('user/refreshToken');
        const token = getToken();
        processQueue(null, token);
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return service(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        console.log("refreshError", refreshError);
        await handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle different error responses
    if (error.response) {
      switch (error.response.status) {
        case 400:
          // Handle .NET validation error format
          if (error.response.data?.errors && typeof error.response.data.errors === 'object') {
            const validationErrors = error.response.data.errors;
            
            // Check if it's the .NET validation error format (field names as keys with array values)
            const fieldNames = Object.keys(validationErrors);
            if (fieldNames.length > 0) {
              const errorMessages = [];
              
              fieldNames.forEach(fieldName => {
                const fieldErrors = validationErrors[fieldName];
                if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
                  // Add each error message from this field
                  fieldErrors.forEach(errorMsg => {
                    if (errorMsg && typeof errorMsg === 'string') {
                      errorMessages.push(errorMsg);
                    }
                  });
                }
              });
              
              // Show the validation errors from API
              if (errorMessages.length > 0) {
                if (errorMessages.length === 1) {
                  store.commit('message/showMessage', { 
                    message: errorMessages[0], 
                    type: "error" 
                  });
                } else {
                  // Multiple validation errors - show them as a list
                  const combinedMessage = errorMessages.join('\n• ');
                  store.commit('message/showMessage', { 
                    message: `• ${combinedMessage}`, 
                    type: "error" 
                  });
                }
                break; // Exit the case after handling validation errors
              }
            }
          }
          
          // Handle ServiceResult format with Errors array
          if (error.response.data?.Errors && Array.isArray(error.response.data.Errors)) {
            const errorMessages = error.response.data.Errors.filter(msg => msg && typeof msg === 'string');
            if (errorMessages.length > 0) {
              if (errorMessages.length === 1) {
                store.commit('message/showMessage', { 
                  message: errorMessages[0], 
                  type: "error" 
                });
              } else {
                const combinedMessage = errorMessages.join('\n• ');
                store.commit('message/showMessage', { 
                  message: `• ${combinedMessage}`, 
                  type: "error" 
                });
              }
              break;
            }
          }
          
          // Existing fallback logic for other 400 error formats
          if (error.response.data?.errors?.$values?.length) {
            store.commit('message/showMessage', { 
              message: error.response.data.errors.$values[0].message, 
              type: "error" 
            });
          } else if (error.response.data?.message) {
            store.commit('message/showMessage', { 
              message: error.response.data.message, 
              type: "error" 
            });
          } else if (error.response.data?.Message) {
            // Handle ServiceResult format with Message
            store.commit('message/showMessage', { 
              message: error.response.data.Message, 
              type: "error" 
            });
          } else if (error.response.data?.title) {
            // Handle .NET problem details format
            store.commit('message/showMessage', { 
              message: error.response.data.title, 
              type: "error" 
            });
          } else {
            store.commit('message/showMessage', { 
              message: "يرجي التاكد من صحة البيانات!", 
              type: "error" 
            });
          }
          break;
        case 401:
        case 406:
          if (error.response.data?.message) {
            store.commit('message/showMessage', { 
              message: error.response.data.message, 
              type: "error" 
            });
          }
          break;
        case 403:
          store.commit('message/showMessage', { 
            message: "غير مصرح لك بالوصول", 
            type: "error" 
          });
          app.$router.push('/not-authorized');
          break;
        case 404:
          store.commit('message/showMessage', { 
            message: "لم يتم العثور على البيانات المطلوبة", 
            type: "error" 
          });
          break;
        case 500:
          store.commit('message/showMessage', { 
            message: "هناك خطا مرجعي حاول فى وقت لاحق", 
            type: "error" 
          });
          break;
        default:
          store.commit('message/showMessage', { 
            message: "حدث خطأ غير متوقع", 
            type: "error" 
          });
      }
    } else if (error.request) {
      store.commit('message/showMessage', { 
        message: "لا يمكن الاتصال بالخادم", 
        type: "error" 
      });
    } else {
      console.log('error', error);
      store.commit('message/showMessage', { 
        message: "حدث خطأ في الطلب", 
        type: "error" 
      });
    }
      
    return Promise.reject(error);
  }
);

const handleLogout = async () => {
  // Clear all authentication data (cookies and session)
  await store.dispatch("user/logout");
  
  // Clear any additional session storage or local storage if needed
  if (typeof window !== 'undefined') {
    // Clear session storage
    sessionStorage.clear();
    
    // Clear specific localStorage items related to auth
    const authKeys = ['Access-Token', 'Refresh-Token', 'Token-Expiration', 'User-Data'];
    authKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  }
  
  // Redirect to login page
  if (app?.$router) {
    app.$router.push(`/login`);
  }
};

export default service;
